import type {Router} from '@inertiajs/core'

export const chunkReloadKey = 'duesy:chunk-reload-url'

type ChunkReloadStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>
type ChunkReloadLocation = {
    readonly href: string
    assign: (url: string) => void
}

export interface ChunkReloadGuard {
    /**
     * Records the destination of an in-flight Inertia visit so a preload failure
     * recovers to the page the user asked for rather than the one they are on.
     */
    trackVisit(url: string | null): void

    /** Clears the bookkeeping once a visit lands successfully. */
    completeVisit(): void

    /**
     * Handles a `vite:preloadError`. Returns `true` when a recovery reload was
     * started, `false` when the loop guard suppressed it.
     */
    handlePreloadError(): boolean
}

/**
 * Resolves the storage used for the reload loop guard, degrading to an in-memory
 * stand-in rather than throwing.
 *
 * Reading `sessionStorage` throws `SecurityError` when storage is blocked
 * (Chrome "block all cookies", restricted webviews, some private-browsing
 * contexts), and writes can throw `QuotaExceededError`. This is resolved during
 * module evaluation at boot, so an unguarded throw would abort the entry module
 * before anything renders. The fallback only survives within one document, so a
 * browser without usable storage loses cross-reload loop protection — it still
 * boots, which is the point.
 */
export function resolveChunkReloadStorage(): ChunkReloadStorage {
    let storage: Storage | null = null

    try {
        storage = globalThis.sessionStorage ?? null
    } catch {
        storage = null
    }

    const fallback = new Map<string, string>()

    return {
        getItem(key: string): string | null {
            try {
                const stored = storage?.getItem(key)

                if (typeof stored === 'string') {
                    return stored
                }
            } catch {
                // Fall through to the in-memory copy.
            }

            return fallback.get(key) ?? null
        },
        setItem(key: string, value: string): void {
            fallback.set(key, value)

            try {
                storage?.setItem(key, value)
            } catch {
                // Quota or private-mode write failures degrade to the in-memory copy.
            }
        },
        removeItem(key: string): void {
            fallback.delete(key)

            try {
                storage?.removeItem(key)
            } catch {
                // Nothing to recover from — the marker is best-effort.
            }
        },
    }
}

/**
 * Tracks chunk-preload failures and recovers by reloading the in-flight
 * destination, with a storage-backed guard so a persistently failing chunk
 * cannot loop.
 *
 * Deliberately does *not* call `event.preventDefault()`. Vite's `__vitePreload`
 * helper ends with `baseModule().catch(handlePreloadError)`, and its
 * `handlePreloadError` only rethrows while the event's default is not prevented.
 * Suppressing it makes the dynamic import *resolve with `undefined`* instead of
 * rejecting, which is what produced `TypeError: undefined is not an object`
 * (Sentry DUESY-3P4). Letting Vite rethrow keeps the failure loud and typed
 * while the recovery navigation proceeds in parallel.
 */
export function createChunkReloadGuard(
    storage: ChunkReloadStorage,
    location: ChunkReloadLocation,
): ChunkReloadGuard {
    let pendingVisitUrl: string | null = null

    return {
        trackVisit(url: string | null): void {
            pendingVisitUrl = url
        },
        completeVisit(): void {
            pendingVisitUrl = null
            storage.removeItem(chunkReloadKey)
        },
        handlePreloadError(): boolean {
            const target = pendingVisitUrl ?? location.href

            if (storage.getItem(chunkReloadKey) === target) {
                return false
            }

            storage.setItem(chunkReloadKey, target)
            location.assign(target)

            return true
        },
    }
}

type PageModule<T> = {default: T} | undefined

/**
 * Awaits a lazily imported Inertia page module and returns its default export.
 *
 * Throws a named error when the module has no usable default export — a page
 * file missing `export default` otherwise fails deep inside React's render with
 * "Element type is invalid", which carries no indication of which page is at
 * fault.
 */
export async function resolvePageModule<T>(
    loader: () => Promise<PageModule<T>>,
    name: string,
): Promise<T> {
    const page = await loader()

    if (!page?.default) {
        throw new Error(`Inertia page component resolved to undefined: ${name}`)
    }

    return page.default
}

/**
 * Resolves an Inertia page name against an entry's `import.meta.glob('./Pages/...')` map.
 */
export function resolveGlobbedPage<T>(
    pages: Record<string, () => Promise<PageModule<T>>>,
    name: string,
): Promise<T> {
    const loader = pages[`./Pages/${name}.tsx`]

    if (!loader) {
        throw new Error(`Unknown Inertia page component: ${name}`)
    }

    return resolvePageModule(loader, name)
}

/**
 * Wires a chunk-reload guard to Inertia's visits and Vite's preload errors, for each entry to
 * call once as it boots. Inertia fires `navigate` for the initial page as well as every visit.
 */
export function installChunkReloadGuard(
    router: Pick<Router, 'on'>,
    target: Pick<Window, 'addEventListener' | 'location'> = window,
): ChunkReloadGuard {
    const guard = createChunkReloadGuard(resolveChunkReloadStorage(), target.location)

    router.on('before', event => {
        guard.trackVisit(event.detail.visit.url.href)
    })

    router.on('navigate', () => {
        guard.completeVisit()
    })

    target.addEventListener('vite:preloadError', () => {
        guard.handlePreloadError()
    })

    return guard
}
